import React from 'react';
import { formatDate } from '../../../utils/dateFormat';

/**
 * TimelineItem - Component for displaying chronological medical events
 * Used in medical history and appointment timelines
 */
interface TimelineItemProps {
  date: string;
  title: string;
  description?: string;
  type: 'appointment' | 'medication' | 'condition' | 'procedure' | 'note';
  status?: 'completed' | 'scheduled' | 'cancelled' | 'in-progress';
  isLast?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
}

const TimelineItem: React.FC<TimelineItemProps> = ({
  date,
  title,
  description,
  type,
  status = 'completed',
  isLast = false,
  onClick,
  children
}) => {
  const typeConfig = {
    appointment: {
      icon: '📅',
      color: 'blue',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-200'
    },
    medication: {
      icon: '💊',
      color: 'green',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-200'
    },
    condition: {
      icon: '🏥',
      color: 'orange',
      bgColor: 'bg-orange-100',
      borderColor: 'border-orange-200'
    },
    procedure: {
      icon: '🔬',
      color: 'purple',
      bgColor: 'bg-purple-100',
      borderColor: 'border-purple-200'
    },
    note: {
      icon: '📝',
      color: 'gray',
      bgColor: 'bg-gray-100',
      borderColor: 'border-gray-200'
    }
  };

  const statusConfig = {
    completed: 'bg-green-500',
    scheduled: 'bg-blue-500',
    cancelled: 'bg-red-500',
    'in-progress': 'bg-yellow-500'
  };

  const config = typeConfig[type];
  const isClickable = !!onClick;

  return (
    <div className="relative flex items-start">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-6 top-12 w-0.5 h-full bg-gray-200" />
      )}

      {/* Timeline dot */}
      <div className="relative flex-shrink-0">
        <div className={`w-12 h-12 rounded-full ${config.bgColor} ${config.borderColor} border-2 flex items-center justify-center`}>
          <span className="text-lg" role="img" aria-hidden="true">
            {config.icon}
          </span>
        </div>
        {/* Status indicator */}
        <div className={`absolute -bottom-1 -right-1 w-4 h-4 ${statusConfig[status]} rounded-full border-2 border-white`} />
      </div>

      {/* Content */}
      <div 
        className={`ml-4 flex-1 ${isClickable ? 'cursor-pointer' : ''}`}
        onClick={onClick}
      >
        <div className={`p-4 rounded-lg border ${config.borderColor} ${config.bgColor} ${isClickable ? 'hover:shadow-sm transition-shadow' : ''}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              {title}
            </h4>
            <time className="text-xs text-gray-500">
              {formatDate(date)}
            </time>
          </div>

          {/* Description */}
          {description && (
            <p className="text-sm text-gray-600 mb-2">
              {description}
            </p>
          )}

          {/* Additional content */}
          {children && (
            <div className="mt-3">
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimelineItem;